import { useState, useEffect, useCallback } from 'react';
import { usersApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Users, Trash2, Shield, User, Plus, Settings, Save, LayoutDashboard, Activity, Truck, MapPin, Scissors, FileText, CalendarDays, DollarSign, Map as MapIcon, Store } from 'lucide-react';

const PAGE_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, description: 'Main overview & weather' },
  { key: 'activities', label: 'Activities', icon: Activity, description: 'Spraying, mowing, fertilizing logs' },
  { key: 'vehicles', label: 'Vehicles & Inventory', icon: Truck, description: 'Fleet, fuel, services, prestarts, stock' },
  { key: 'locations', label: 'Fields', icon: MapPin, description: 'Paddock mapping & field history' },
  { key: 'turf_cuts', label: 'Turf Cuts', icon: Scissors, description: 'Harvest tracking with GPS' },
  { key: 'reports', label: 'Reports', icon: FileText, description: 'Data export & CSV reports' },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays, description: 'Maintenance scheduling & events' },
  { key: 'costs', label: 'Costs', icon: DollarSign, description: 'Cost tracking & analytics' },
  { key: 'farm_map', label: 'Farm Map', icon: MapIcon, description: 'Farm overview map with all fields & cut areas' },
  { key: 'store', label: 'Turf Shop & Orders', icon: Store, description: 'Manage shop varieties, photos & customer orders' },
  { key: 'team', label: 'User Management', icon: Users, description: 'Manage users & permissions' },
];

export function TeamPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permEdits, setPermEdits] = useState([]);
  const [saving, setSaving] = useState(false);
  const { user: currentUser, checkAuth } = useAuth();

  // Add user form
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('worker');

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersApi.getAll();
      setUsers(res.data);
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await usersApi.create({
        name: newName,
        email: newEmail,
        password: newPassword,
        role: newRole,
      });
      toast.success(`${newName} added to the team`);
      setAddDialogOpen(false);
      setNewName(''); setNewEmail(''); setNewPassword(''); setNewRole('worker');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('Team member removed');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const openPermissions = (user) => {
    setSelectedUser(user);
    setPermEdits([...(user.permissions || [])]);
    setPermDialogOpen(true);
  };

  const togglePermission = (key) => {
    setPermEdits(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const defaultPerms = newRole === 'admin'
        ? PAGE_PERMISSIONS.map(p => p.key)
        : ['dashboard', 'activities', 'vehicles', 'locations', 'turf_cuts'];
      await usersApi.update(userId, { role: newRole, permissions: defaultPerms });
      toast.success(`Role updated to ${newRole}`);
      loadUsers();
      if (userId === currentUser?.id) checkAuth();
    } catch {
      toast.error('Failed to update role');
    }
  };

  const savePermissions = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await usersApi.update(selectedUser.id, { permissions: permEdits });
      toast.success(`Permissions updated for ${selectedUser.name}`);
      setPermDialogOpen(false);
      loadUsers();
      if (selectedUser.id === currentUser?.id) checkAuth();
    } catch {
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="team-loading" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground">Manage team members, roles, and page access permissions</p>
        </div>
        <Button className="btn-primary" onClick={() => setAddDialogOpen(true)} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Users className="h-10 w-10 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Members</p>
                <p className="text-2xl font-heading font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Shield className="h-10 w-10 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-heading font-bold">{users.filter(u => u.role === 'admin').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <User className="h-10 w-10 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Workers</p>
                <p className="text-2xl font-heading font-bold">{users.filter(u => u.role === 'worker').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card data-testid="team-table">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Access</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p>{user.name}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-muted-foreground">(You)</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    {user.id === currentUser?.id ? (
                      <Badge variant="default">
                        <Shield className="h-3 w-3 mr-1" /> {user.role}
                      </Badge>
                    ) : (
                      <Select value={user.role} onValueChange={(v) => handleRoleChange(user.id, v)}>
                        <SelectTrigger className="w-[110px] h-8" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="worker">Worker</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.permissions || []).slice(0, 3).map(p => (
                        <Badge key={p} variant="outline" className="text-[10px] px-1.5 py-0">
                          {PAGE_PERMISSIONS.find(pp => pp.key === p)?.label || p}
                        </Badge>
                      ))}
                      {(user.permissions || []).length > 3 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          +{user.permissions.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openPermissions(user)}
                        data-testid={`edit-perms-${user.id}`}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} required placeholder="Full name" data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required placeholder="email@example.com" data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="Minimum 6 characters" data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                  <SelectItem value="worker">Worker (limited access)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {newRole === 'admin' ? 'Admins have access to all pages including user management' : 'Workers get access to Dashboard, Activities, Vehicles, Fields, and Turf Cuts by default'}
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="submit-new-user">Add User</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="permissions-dialog">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-3">
                  <Settings className="h-5 w-5" />
                  Permissions — {selectedUser.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant={selectedUser.role === 'admin' ? 'default' : 'secondary'}>
                    {selectedUser.role === 'admin' && <Shield className="h-3 w-3 mr-1" />}
                    {selectedUser.role}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{selectedUser.email}</span>
                </div>

                <p className="text-sm font-medium mb-3">Page Access</p>
                <div className="space-y-3">
                  {PAGE_PERMISSIONS.map((perm) => {
                    const Icon = perm.icon;
                    const isEnabled = permEdits.includes(perm.key);
                    return (
                      <div key={perm.key} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-secondary/20 transition-colors">
                        <div className="flex items-center gap-3">
                          <Icon className={`h-5 w-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div>
                            <p className={`text-sm font-medium ${isEnabled ? '' : 'text-muted-foreground'}`}>{perm.label}</p>
                            <p className="text-xs text-muted-foreground">{perm.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => togglePermission(perm.key)}
                          data-testid={`perm-toggle-${perm.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setPermDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={savePermissions} disabled={saving} className="flex-1 btn-primary" data-testid="save-permissions-btn">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Permissions'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
